import Button from 'molecules/Button';
import Icon from 'molecules/Icon';
import Image from 'molecules/Image';
import { splitSectionProps } from 'molecules/Section/config';

import type { ConnectMastheadConfigProps } from 'components/ConnectMasthead/config';
import type { FC } from 'react';

const ConnectMasthead: FC<ConnectMastheadConfigProps> = props => {
  const {
    sectionProps,
    rest: { wordmark, wordmarkMobile, body, dateText, location, button, image, watermarkImage },
  } = splitSectionProps(props);

  const mobileWordmark = wordmarkMobile?.file?.url ? wordmarkMobile : wordmark;

  // A custom <section> (not the Section molecule) so the brand watermark is a
  // direct child of the unpadded section box: the molecule's own pt/pb padding
  // never shifts the watermark, only `overflow-hidden` clips its off-edge bleed.
  return (
    <section id={sectionProps.id} className="dark relative scroll-mt-16 overflow-hidden bg-gray-900">
      {/* Brand watermark: a large faint mark bleeding off the right edge, behind
          the content. It sits OUTSIDE the padded content container, so it is
          anchored to the true section box and is unaffected by content padding. */}
      {watermarkImage?.file?.url && (
        <Image
          url={watermarkImage.file.url}
          alt=""
          noFill
          unsetMaxW
          unsetRatio
          width={watermarkImage.file.width}
          height={watermarkImage.file.height}
          sizes="(min-width: 640px) 480px, 640px"
          className="pointer-events-none absolute right-0 top-0 z-0 h-auto w-[140%] max-w-none translate-x-[15%] sm:w-[30rem] sm:translate-x-0"
        />
      )}

      <div className="relative z-10 mx-auto flex w-full max-w-screen-xl flex-col gap-10 px-8 pb-12 pt-6 sm:px-32 sm:pb-16 sm:pt-8 lg:gap-12 lg:px-8 lg:pb-24 lg:pt-14">
        {/* Wordmark (the desktop image bakes in the Prismatic logo + gradient) */}
        {wordmark?.file?.url && (
          <Image
            url={wordmark.file.url}
            alt={wordmark.alt || 'Connect ‘26'}
            noFill
            unsetMaxW
            unsetRatio
            priority
            width={wordmark.file.width}
            height={wordmark.file.height}
            sizes="(min-width: 1024px) 1024px, (min-width: 640px) 90vw, 1px"
            className="hidden h-auto w-full max-w-5xl sm:block"
          />
        )}
        {mobileWordmark?.file?.url && (
          <Image
            url={mobileWordmark.file.url}
            alt={mobileWordmark.alt || 'Connect ‘26'}
            noFill
            unsetMaxW
            unsetRatio
            priority
            width={mobileWordmark.file.width}
            height={mobileWordmark.file.height}
            sizes="(max-width: 639px) 75vw, 1px"
            className="h-auto w-full sm:hidden"
          />
        )}

        <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-12">
          {/* Left: copy, date/location, CTA */}
          <div className="flex flex-col items-start gap-6">
            {body && <p className="max-w-xl text-[26px] leading-[39px] text-gray-300">{body}</p>}

            {(dateText || location) && (
              <div className="flex flex-col gap-3">
                {dateText && (
                  <div className="flex items-center gap-2.5 text-md text-gray-300">
                    <Icon icon="calendar-date" iconColor="text-primary-300" size={20} />
                    <span>{dateText}</span>
                  </div>
                )}
                {location && (
                  <div className="flex items-center gap-2.5 text-md text-gray-300">
                    <Icon icon="marker-pin-01" iconColor="text-primary-300" size={20} />
                    <span>{location}</span>
                  </div>
                )}
              </div>
            )}

            {button?.label && (
              <Button label={button.label} href={button.href} hierarchy="primary" className="w-full sm:w-fit" />
            )}
          </div>

          {/* Right: hero image (the audio-style overlay is baked into the image) */}
          {image?.file?.url && (
            <Image
              url={image.file.url}
              alt={image.alt || ''}
              noFill
              unsetMaxW
              unsetRatio
              priority
              width={image.file.width}
              height={image.file.height}
              sizes="(min-width: 1024px) 600px, (min-width: 640px) 90vw, 90vw"
              className="h-auto w-full rounded-2xl"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default ConnectMasthead;
